import { useRetryTask } from '@dispatch/shared/hooks';
import { NaqRetryResponse } from '@dispatch/shared/types';
import { useNotification } from '@dispatch/web/hooks';
import { Replay } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { useState } from 'react';

export type TaskRetryButtonProps = {
  taskId: string;
  disabled?: boolean;
};

type RetryErrorShape = {
  response?: {
    data?: NaqRetryResponse;
  };
};

const FAILURE_REASON_LABELS: Record<string, string> = {
  feature_not_enabled: 'NAQ self-service is not enabled for this tenant.',
  task_locked: 'Task is locked - unlock it and try again.',
  not_diagnosed:
    'This task was never analyzed for automatic recovery - reassign it manually.',
  no_known_cause:
    'No automatic fix is available for this task - reassign it manually.',
  not_eligible: 'This task is not eligible for retry.',
};

function labelForFailureReason(reason: string | null | undefined): string {
  if (!reason || reason.length === 0) {
    return 'Retry failed.';
  }
  return FAILURE_REASON_LABELS[reason] ?? reason;
}

function extractFailureReason(error: unknown): string {
  return labelForFailureReason(
    (error as RetryErrorShape | undefined)?.response?.data?.failureReason
  );
}

export function TaskRetryButton({ taskId, disabled }: TaskRetryButtonProps) {
  const { notifySuccess, notifyError } = useNotification();
  // Retry is one-shot per ticket 94552: once an attempt fails, the same payload
  // must not be retried again. We disable the button rather than remove it; a
  // page reload remounts this component and clears the flag, which is acceptable.
  const [retryFailed, setRetryFailed] = useState(false);

  const mutation = useRetryTask(
    (data: NaqRetryResponse) => {
      // Treat anything other than an explicit success === true as a failure,
      // so a null/undefined success field doesn't get silently treated as a win.
      if (data?.success !== true) {
        setRetryFailed(true);
        notifyError('Retry failed', labelForFailureReason(data?.failureReason));
        return;
      }
      notifySuccess(
        'Retry submitted',
        'NAQ retry completed successfully.',
        undefined
      );
    },
    (error: unknown) => {
      setRetryFailed(true);
      notifyError('Retry failed', extractFailureReason(error));
    }
  );

  const isLoading = mutation.isLoading;

  return (
    <Stack px={1}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<Replay />}
        data-testid="task-retry-button"
        disabled={disabled || isLoading || retryFailed}
        onClick={() => {
          mutation.mutate(taskId);
        }}
        sx={{
          fontWeight: '700',
          lineHeight: '1.3750em',
          letterSpacing: '0.029em',
        }}
      >
        {isLoading ? 'Retrying...' : 'Retry'}
      </Button>
    </Stack>
  );
}
